import "./App.css";
import React, { useState, useEffect } from "react";
import { HashRouter as Router, Routes, Route, Outlet, useLocation, Navigate } from "react-router-dom";
import SplashScreen from "./Components/SplashScreen";
import { FavoritesProvider } from "./Components/Context/FavoritesContext.js";
import Construtoras from "./Components/Construtoras";
import Home from "./pages/Home";
import Imovel from "./pages/Imovel";
import Header from "./Components/Header";
import LancamentosPlanta from "./pages/LancamentosPlanta.js";

import ListaImoveis from "./admin/pages/ListaImoveis.js";
import SideBar from "./admin/Components/SideBar";
import Dashboard from "./admin/pages/Dashboard.js";
import AddImovel from "./admin/pages/AddImovel.js";
import EditImovel from "./admin/pages/EditImovel.js";
import Config from "./admin/pages/Config.js";
import Clientes from "./admin/pages/Clientes.js";
import ComprovantesAlugueis from "./admin/pages/ComprovanteAlugueis.js";
import ComprovantesVenda from "./admin/pages/ComprovanteVendas.js";
import FichaCaptacao from "./admin/pages/FichaCaptacao.js";
import EmprestimoChave from "./admin/pages/EmprestimoChaves.js";

import Footer from "./Components/Footer.js";
import ComprarImovel from "./pages/ComprarImovel.js";
import AlugarImovel from "./pages/AlugarImovel.js";
import TodosImoveis from "./pages/TodosImoveis.js";
import FaleConosco from "./pages/FaleConosco.js";
import Pesquisa from "./pages/Pesquisa";
import Login from "./admin/pages/Login";
import PrivateRoute from "./admin/Components/PrivateRoute.js";
import CadastrarCorretor from "./admin/pages/CadastrarCorretor.js";
import ScrollToTop from "./Components/ScrollToTop.js";
import Corretores from "./pages/Corretores.js";

import { db } from "./firebaseConfig";
import { ref as databaseRef, onValue } from "firebase/database";

function RedirectFromShare() {
  const location = useLocation();

  useEffect(() => {
    // Extrai os parâmetros da URL (ex: ?id=123&title=...&desc=...)
    const params = new URLSearchParams(location.search);
    const id = params.get("id");

    if (id) {
      // Redireciona para a rota do imóvel (HashRouter)
      window.location.replace(`/#/imovel/${id}`);
    } else {
      // Se não tiver ID, volta pra home
      window.location.replace("/#/");
    }
  }, [location]);

  return null;
}

function App() {
  const [isSplashVisible, setIsSplashVisible] = useState(true);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [areImagesLoaded, setAreImagesLoaded] = useState(false);

  // Espera os imóveis carregarem do Firebase
  useEffect(() => {
    const imoveisRef = databaseRef(db, "imoveis/");

    const unsubscribe = onValue(imoveisRef, (snapshot) => {
      setIsDataLoaded(true);
    });

    // Fallback: força saída da splash em 8s mesmo se algo travar
    const timeout = setTimeout(() => setIsSplashVisible(false), 8000);

    return () => {
      clearTimeout(timeout);
      unsubscribe();
    };
  }, []);

  // Espera as imagens carregarem de fato
  useEffect(() => {
    if (!isDataLoaded) return;

    const handleImagesLoaded = () => {
      const images = document.querySelectorAll("img");
      if (images.length === 0) {
        setAreImagesLoaded(true);
        return;
      }

      let loaded = 0;
      const checkLoaded = () => {
        loaded++;
        if (loaded === images.length) setAreImagesLoaded(true);
      };

      images.forEach((img) => {
        if (img.complete) {
          loaded++;
        } else {
          img.addEventListener("load", checkLoaded);
          img.addEventListener("error", checkLoaded);
        }
      });

      if (loaded === images.length) setAreImagesLoaded(true);
    };

    // pequeno delay pra garantir que o DOM foi renderizado
    const delay = setTimeout(handleImagesLoaded, 500);
    return () => clearTimeout(delay);
  }, [isDataLoaded]);

  // Remove splash apenas quando tudo estiver realmente carregado
  useEffect(() => {
    if (isDataLoaded && areImagesLoaded) {
      const delay = setTimeout(() => setIsSplashVisible(false), 800);
      return () => clearTimeout(delay);
    }
  }, [isDataLoaded, areImagesLoaded]);

  if (isSplashVisible) {
    return <SplashScreen />;
  }

  // Layout do painel admin
  const AdminLayout = () => (
    <div style={{ display: "flex" }}>
      <SideBar />
      <div style={{ flex: 1, padding: "20px" }}>
        <Outlet />
      </div>
    </div>
  );

  return (
    <FavoritesProvider>
      <Router>
        <ScrollToTop />
        <div className="App">
          <Routes>
            {/* --- Rota que intercepta /share.php?id=... --- */}
            <Route path="/share.php" element={<RedirectFromShare />} />

            {/* --- Demais rotas inalteradas --- */}
            <Route path="/admin/login" element={<Login />} />
            <Route path="/panel" element={<PrivateRoute />}>
              <Route element={<AdminLayout />}>
                <Route index element={<Dashboard />} />
                <Route path="dashboard" element={<Dashboard />} />
                <Route path="lista-imoveis" element={<ListaImoveis />} />
                <Route path="AddImovel" element={<AddImovel />} />
                <Route path="Clientes" element={<Clientes />} />
                <Route path="EditImovel/:id" element={<EditImovel />} />
                <Route path="cadastrar-corretor" element={<CadastrarCorretor />} />
                <Route path="Config" element={<Config />} />
                <Route path="comprovantes-aluguel" element={<ComprovantesAlugueis />} />
                <Route path="comprovantes-venda" element={<ComprovantesVenda />} />
                <Route path="ficha-captacao" element={<FichaCaptacao />} />
                <Route path="emprestimo-chaves" element={<EmprestimoChave />} />
              </Route>
            </Route>

            <Route path="/" element={<><Header /><Home /><Footer /></>} />
            <Route path="/construtoras" element={<><Header /><Construtoras /><Footer /></>} />
            <Route path="/imovel/:codigo" element={<><Header /><Imovel /><Footer /></>} />
            <Route path="/todos-imoveis" element={<><Header /><TodosImoveis /><Footer /></>} />
            <Route path="/lancamentos-planta" element={<><Header /><LancamentosPlanta /><Footer /></>} />
            <Route path="/comprar-imovel" element={<><Header /><ComprarImovel /><Footer /></>} />
            <Route path="/alugar-imovel" element={<><Header /><AlugarImovel /><Footer /></>} />
            <Route path="/fale-conosco" element={<><Header /><FaleConosco /><Footer /></>} />
            <Route path="/corretores" element={<><Header /><Corretores /><Footer /></>} />
            <Route path="/pesquisa" element={<><Header /><Pesquisa /><Footer /></>} />
          </Routes>
        </div>
      </Router>
    </FavoritesProvider>
  );
}

export default App;
